import { AI_REVIEW_MARK, isAiReviewAccount } from './aiReviewMark';
import type { AiReviewConfig } from './aiReviewTypes';
import type { SwingAccountKey, SwingConfig } from './swingTypes';

/**
 * 국내 스윙 계좌 도움말("?") — 설정 표 · 본문 · 용어. (2026-10-10 · docs/impl/141 12.6-5 · 사용자 "? 도움말도 스윙 계좌에 맞게")
 *
 * 🔴 숫자는 전부 서버 설정(SwingConfig)에서 받는다 — 화면에 박으면 trading.yml 을 고쳤을 때 도움말이 거짓말이 된다
 *   (CLAUDE.md 6절 9항 · 저점매수 bandSettings 와 같은 원칙)
 * 🔴 입문자가 읽는다 — 전문 용어는 그 자리에서 풀고, 실제로 화면에 있는 것만 설명한다(메모리 explain-jargon-in-ui)
 */

export interface SwingHelpSetting {
  label: string;
  value: string;
}

export interface SwingHelpSection {
  heading: string;
  body: string;
}

/** 원 → "200만원" (만 단위 반올림) */
function manwon(won: number): string {
  return `${Math.round(won / 10_000).toLocaleString('ko-KR')}만원`;
}

/** 단 비중 → "200만 · 100만 · 100만원" */
function legBudgetsText(config: SwingConfig): string {
  return config.legBudgets.map((b) => `${Math.round(b / 10_000).toLocaleString('ko-KR')}만`).join(' · ') + '원';
}

/** 지금 설정 표 */
export function swingSettings(config: SwingConfig | null | undefined): SwingHelpSetting[] {
  if (config === undefined || config === null) {
    return [{ label: '설정', value: '불러오는 중…' }];
  }
  const [first, second, third] = config.ladderPcts;
  // 🇺🇸 미국 스윙(2026-10-10) — 가격 · 규칙은 달러, 한도는 원화를 살 때 그 순간 환율로 바꾼다 · 거래일은 미국 거래일
  const us = config.currency === 'USD';
  return [
    {
      label: '감시 대상',
      value: us
        ? '미국 대형주 83종목 중 다섯 섹터로 손분류해 둔 종목에서 섹터마다 다섯 종목(매월 첫 미국 거래일에 다시 고름)'
        : '코스피 고정 섹터 × 섹터마다 다섯 종목(매월 첫 거래일에 다시 고름)',
    },
    { label: '1단 진입선', value: `최근 ${config.window}거래일(${config.windowLabel}) 가격 범위의 하위 ${config.buyPercentile}%` },
    { label: '종목당 한도', value: us ? `${manwon(config.stockCap)}(살 때 그 순간 환율로 달러로 바꿈)` : `${manwon(config.stockCap)}(산 가격 기준)` },
    { label: '단별 금액', value: `${config.maxLegs}단 — ${legBudgetsText(config)}` },
    { label: '추가 매수', value: `직전 단 가격보다 ${config.legAtr} ATR 더 내려올 때마다 다음 단` },
    { label: '보유 상한', value: `동시에 ${config.maxPositions}종목 · 섹터마다 ${config.sectorHold}종목` },
    { label: '손절선', value: `진입선 − ${config.stopAtr} ATR (산 날 고정)` },
    { label: '손절 유예', value: `산 날부터 ${config.graceDays}거래일 동안은 손절선 · 본전선을 보지 않음` },
    { label: '계단 익절', value: `평단 +${first}% · +${second}% · +${third}% 에서 1/3 · 1/3 · 나머지 (하루 한 단)` },
    { label: '본전선', value: '첫 익절 뒤에는 평단 아래로 내려오면 남은 것을 팖' },
    {
      label: '만료',
      value: `산 날부터 ${us ? '미국 ' : ''}${config.maxHoldDays}거래일째 ${config.expireAfter} 뒤에 손익과 상관없이 정리`,
    },
    { label: '손절 뒤 재매수', value: `직전 손절가보다 ${config.reentryAtr} ATR 더 아래로 와야 다시 삼` },
  ];
}

/**
 * 본문 — 숫자는 설정에서, 섹터 이름은 응답의 섹터 목록에서
 *
 * @param sectorNames 고정 섹터 이름(SwingStatus.sectors) · 없으면 "다섯 섹터" 로만 쓴다
 */
export function swingSections(config: SwingConfig | null | undefined, sectorNames: string[] = []): SwingHelpSection[] {
  const sectorsText = sectorNames.length > 0 ? `${sectorNames.join(' · ')} ${sectorNames.length}개 섹터` : '다섯 섹터';
  const window = config ? `${config.window}거래일(${config.windowLabel})` : '정해 둔 기간';
  const pct = config ? `하위 ${config.buyPercentile}%` : '아래쪽';
  const grace = config ? `${config.graceDays}거래일` : '정해 둔 기간';
  const hold = config ? `${config.maxHoldDays}거래일` : '정해 둔 기간';
  const cap = config ? manwon(config.stockCap) : '종목당 한도';
  const sectorHold = config ? `${config.sectorHold}종목` : '정해 둔 수';
  const legs = config ? legBudgetsText(config) : '세 번';
  const ladder = config ? `+${config.ladderPcts.join('% · +')}%` : '정해 둔 폭';
  const us = config?.currency === 'USD';
  const sections: SwingHelpSection[] = [
    {
      heading: '어떤 종목을 보나',
      body:
        (us ? `미국 대형주 83종목 중 일부를 ${sectorsText}로 손으로 나눠 두고(공식 업종 분류가 아닙니다), ` : `코스피에서 ${sectorsText}를 정해 두고, `) +
        `섹터마다 거래가 많고 자주 오르내린(왕복성이 높은) 다섯 종목을 매월 첫 ${us ? '미국 ' : ''}거래일에 고릅니다. ` +
        '감시 대상 표 위의 섹터 버튼으로 한 섹터만 볼 수 있고, 그때 머리에 그 섹터를 몇 종목 들고 있는지(예: 보유 2/4)가 나옵니다.',
    },
    {
      heading: '언제 사나',
      body:
        `종목마다 최근 ${window} 동안의 가장 낮은 값과 가장 높은 값 사이에서 ${pct} 지점을 1단 진입선으로 잡습니다. ` +
        `현재가가 그 아래로 오면 1단을 사고, 더 떨어지면 나눠서 더 삽니다(${legs}). ` +
        `한 종목에 넣는 돈은 ${cap}을 넘지 않습니다. 같은 섹터는 ${sectorHold}까지만 들고, ` +
        '새 종목을 살 때는 이미 든 종목이 더 떨어질 때 살 돈(예약)을 남겨 둡니다.',
    },
    {
      heading: '언제 파나',
      body:
        `평단에서 ${ladder} 오를 때마다 1/3씩 나눠 팝니다(하루에 한 단만). 들고 있는 수가 적어 1/3 이 0주이면 그 단은 건너뛰어 ` +
        '사실상 마지막 단에서 한 번에 팝니다. 한 번이라도 익절했으면 그 뒤로는 평단(본전) 아래로 내려올 때 남은 것을 팝니다. ' +
        `산 지 ${grace} 동안은 손절선 아래로 내려가도 기다립니다 — 사자마자 흔들려 파는 것을 막는 규칙입니다. ` +
        `그래도 ${hold}째가 되면 손익과 상관없이 정리합니다.`,
    },
    {
      heading: '표에서 "못 사는 이유" 는 무엇인가',
      body:
        '진입선 아래로 왔는데도 오늘 사지 못한 종목에 그 이유를 적습니다. 화면이 따로 계산한 것이 아니라 ' +
        '매매 엔진이 그날 실제로 남긴 기록입니다 — 섹터가 다 찼다 · 보유 상한이 찼다 · 예수금이 모자라다 · ' +
        '다른 종목 추가 매수 몫으로 남겨 두었다 · 1주가 종목 한도를 넘는다 · 손절 뒤 재진입선 위다 · 오늘 판 종목이다.',
    },
  ];
  if (us) {
    // 🇺🇸 미국판 — 145번 8절 "forward 기록 머리에 고정할 문구"(국내 규칙을 값 그대로 옮긴 시험 계좌 · 생존자 표본 · 폭락일 노출)
    sections.push(
      {
        heading: '미국 계좌라서 다른 것',
        body:
          '가격과 규칙(진입선 · 손절선 · 익절 · 다음 단)은 달러로 계산하고, 예수금은 원화입니다. 살 때 · 팔 때 그 순간 환율로 바꾸며, ' +
          '계좌 카드의 총자산 · 실현 손익은 환율 몫까지 들어간 원화입니다. 매매는 한국 시각 밤에 미국 정규장에서만 일어나고, ' +
          '유예 · 만료의 거래일은 미국 거래일(미국 동부 날짜)로 셉니다.',
      },
      {
        heading: '이 계좌를 읽을 때',
        body:
          '국내 스윙(6개월) 규칙을 값 그대로 옮긴 시험 계좌입니다. 과거 미국 측정은 지금(2026년) 살아남은 83종목으로 한 것이라 ' +
          '수익의 근거가 되지 않습니다. 평균적으로는 돈을 조금만 넣고 있지만, 과거 폭락일(2020-03-18)에는 넣은 비율이 85 ~ 99% 까지 몰렸던 규칙입니다.',
      },
    );
  } else {
    sections.push({
      heading: '두 계좌는 무엇이 다른가',
      body:
        '스윙(6개월)과 스윙(3개월)은 진입선을 잡는 기간(최근 6개월 · 3개월)과 단별 금액 비율만 다르고 나머지 규칙은 같습니다. ' +
        '어느 쪽이 나은지는 몇 회차로는 가릴 수 없다는 측정 결과가 있어(가르려면 10년 넘게 필요) 승패를 판정하지 않고 나란히 기록합니다.',
    });
  }
  return sections;
}

/**
 * 🔎 AI 2차 검토와의 관계 — 스윙 도움말 마지막 절(2026-10-18 사용자 결정 · docs/impl/153 19.1 · 41.8 · AiEpisodeRules).
 *
 * 스윙(6개월)만 대상이다 — 감시가 SWING_A 감시 표만 본다. 스윙(3개월) · 미국 스윙은 "대상이 아니다" 한 줄.
 * 🔴 숫자(깊은 단 ATR · 정기 점검 거래일 · 하루 호출 상한)는 서버 설정(AiReviewConfig)에서 · 못 받았으면 숫자 없이(I-10)
 * 🔴 보고서가 계좌 매매를 바꾸지 않는다는 줄은 빼지 않는다(CLAUDE.md 1.1 · 1.3)
 *
 * @param ai 저점 신호 검토 응답의 설정 — SWING_A 일 때만 넘긴다
 */
export function swingAiReviewSection(account: SwingAccountKey, ai: AiReviewConfig | null | undefined): SwingHelpSection {
  // 🔎 는 "AI 2차 검토 대상" 표식이라 대상이 아닌 계좌의 제목엔 붙이지 않는다(화면 점검 10/18 · aiReviewMark 머리 주석)
  if (!isAiReviewAccount(account)) {
    return { heading: 'AI 2차 검토와의 관계', body: 'AI 2차 검토 대상이 아닙니다 — 국내 스윙(6개월) 계좌만 대상입니다.' };
  }
  const heading = `${AI_REVIEW_MARK} AI 2차 검토와의 관계`;
  const deeper =
    ai?.leg2Atr != null && ai.leg3Atr != null
      ? `더 깊은 2단선 · 3단선(국면을 연 날 고정한 1단선에서 ${ai.leg2Atr} · ${ai.leg3Atr} ATR 아래)`
      : '더 깊은 2단선 · 3단선';
  const heartbeat = ai ? `${ai.heartbeatDays}거래일` : '정해 둔 거래일';
  const cap = ai ? `하루 AI 호출 상한(이 기능 하루 ${ai.llmDailyCallCap}회 · 앱 하루 한도)` : '하루 AI 호출 상한';
  // ✏️ 2026-10-19 — 관리자 화면에서 켜고 끌 수 있게 됐다(V158) · 켜도 하루 상한 안에서만 붙는다
  const off = ai && !ai.llmEnabled
    ? ' 지금은 AI 호출이 꺼져 있어 숫자 · 공시 · 기사 기록만 남습니다 — 관리자가 [관리자 › AI 비용]에서 켜면 하루 상한 안에서 AI 의견이 붙습니다.'
    : '';
  return {
    heading,
    body:
      '언제 생기나 — 이 계좌의 감시 종목 현재가가 1단선(가격 범위 하위 분위의 기준가 — 손절 뒤 재진입을 기다리는 종목은 계좌가 이보다 낮은 진입선에서 삽니다)에 ' +
      '처음 닿는 순간 기록이 하나 생깁니다(국면 시작). 같은 국면 안에서 선 근처를 오르내리며 다시 닿아도 새로 만들지 않습니다. ' +
      `${deeper}에 처음 닿을 때 · 새 중요 공시가 올라왔을 때 · 마지막 기록 뒤 ${heartbeat}이 지났는데 아직 1단선 아래일 때 갱신합니다(종목당 하루 한 번 — 마지막 경우에 그사이 새 공시 · 기사가 없으면 AI 를 부르지 않고 "새 공시 · 기사 없음" 만 남깁니다). ` +
      `${cap}에 밀린 기록은 다음 거래일 저녁에 국면이 아직 열려 있으면 한 번만 다시 써서 "늦은 보고서" 로 붙습니다.${off} ` +
      `어디서 보나 — [추천] › [${AI_REVIEW_MARK} 저점 신호 검토] 탭, 또는 감시 대상 표 종목 이름 옆 ${AI_REVIEW_MARK} 칩(국면 며칠째)을 누르면 그 기록으로 바로 갑니다. ` +
      '계좌 매매와 무관합니다 — 보고서는 이 계좌의 실제 매수 · 매도를 바꾸지 않고, 계좌는 위 규칙대로만 사고팝니다.',
  };
}

/** 용어 풀이 — 숫자를 넣지 않는 고정 문구(값은 위 설정 표를 가리킨다) */
export const SWING_GLOSSARY: { term: string; meaning: string }[] = [
  {
    term: '진입선 · 하위 n%',
    meaning:
      '최근 기간의 가장 낮은 값을 0, 가장 높은 값을 100 으로 놓았을 때 아래에서 n 지점의 가격입니다. ' +
      '하위 20% 라면 그 기간 가격 폭의 아래쪽 5분의 1 지점입니다.',
  },
  {
    term: 'ATR',
    meaning:
      '그 종목이 하루에 보통 얼마나 움직이는지를 나타내는 값입니다. 많이 움직이는 종목은 손절선 · 추가 매수 간격도 ' +
      '그만큼 넓게 잡혀야 공평해서, 금액이 아니라 ATR 의 배수로 선을 긋습니다.',
  },
  {
    term: '계단 익절',
    meaning: '한 번에 다 팔지 않고 오를 때마다 나눠 파는 것입니다. 몇 % 에서 얼마나 파는지는 위 설정 표에 있습니다.',
  },
  {
    term: '손절 유예',
    meaning:
      '산 직후 며칠은 손절선 아래로 내려가도 팔지 않고 기다리는 기간입니다. 진입 직후 흔들림에 팔고 다시 오르는 것을 막습니다.',
  },
  {
    term: '본전선',
    meaning: '한 번이라도 익절한 뒤에는 손절선이 평단으로 올라옵니다. 그 아래로 오면 남은 것을 팔아 손실로 끝나지 않게 합니다.',
  },
  {
    term: '만료',
    meaning: '정해 둔 거래일 수를 넘겨 들고 있지 않도록 손익과 상관없이 정리하는 것입니다.',
  },
  {
    term: '거래일',
    meaning: '주말 · 공휴일을 뺀, 장이 열린 날만 센 날짜입니다. 보유 표의 "유예 · 만료" 남은 날도 거래일입니다.',
  },
];
